import { Component, LOCALE_ID, OnDestroy, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { createProfileViewOptions } from './options';
import { ButtonComponent } from '../../../components/button/button.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { BackendService } from '../../../api/bff/backend.service';
import { AuthService } from '../../../state/auth/auth.service';
import { ModalService } from '../../../state/modal/modal.service';
import { User } from '@shared/interfaces/user';
import { AnalyticsService } from '../../../state/analytics/analytics.service';

@Component({
  selector: 'app-profile-view',
  imports: [ReactiveFormsModule, ButtonComponent],
  templateUrl: './profile-view.component.html',
  styleUrl: './profile-view.component.css',
})
export class ProfileViewComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private backend = inject(BackendService);
  private authService = inject(AuthService);
  private modalService = inject(ModalService);
  private analyticsService = inject(AnalyticsService);
  protected form = signal<FormGroup | undefined>(undefined);
  protected role = signal<string | null | undefined>(undefined);
  protected options = createProfileViewOptions(inject(LOCALE_ID));
  private subscriptions: Subscription[] = [];

  constructor() {
    this.authService.user.then((user) => {
      if (!user) return;
      const form = this.fb.group({
        country: [user.country, Validators.required],
        yearsInIndustry: [user.yearsInIndustry, Validators.required],
        role: [user.role, Validators.required],
      });
      this.form.set(form);
      this.role.set(user.role);
      this.setupConditionalFields(user);

      this.subscriptions.push(
        form.get('role')?.valueChanges.subscribe((role) => {
          this.role.set(role);
          this.updateConditionalFields(role);
        }) as Subscription,
      );
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  shouldConfirmModalClose() {
    return this.form()?.dirty ?? false;
  }

  async updateProfile() {
    const form = this.form();
    if (form?.valid) {
      const user = await this.authService.user;
      const updatedUser = await firstValueFrom(this.backend.putUser({ ...user, ...form.value }));
      this.authService.setUser(updatedUser);
      this.analyticsService.setUserData(updatedUser);
      this.modalService.closeModal();
    }
  }

  private updateConditionalFields(role: string | null) {
    const form = this.form();
    if (!form) return;
    form.removeControl('sizeUnit');
    form.removeControl('farmSize');
    form.removeControl('companySize');
    form.removeControl('companyName');
    form.removeControl('livestock');

    if (role === 'farm-owner' || role === 'farm-worker') {
      form.addControl('sizeUnit', this.fb.control('ha', Validators.required));
      form.addControl('farmSize', this.fb.control('', Validators.required));
      form.addControl('livestock', this.fb.control('', Validators.required));
    } else if (role === 'contractor-owner' || role === 'contractor-worker') {
      form.addControl('companySize', this.fb.control('', Validators.required));
    } else if (role === 'dealer' || role === 'consultant') {
      form.addControl('companyName', this.fb.control('', Validators.required));
    }
  }

  private setupConditionalFields({ role, sizeUnit, farmSize, livestock, companyName, companySize }: User) {
    const form = this.form();
    if (!form) return;
    if (role === 'farm-owner' || role === 'farm-worker') {
      form.addControl('sizeUnit', this.fb.control(sizeUnit, Validators.required));
      form.addControl('farmSize', this.fb.control(farmSize, Validators.required));
      form.addControl('livestock', this.fb.control(livestock, Validators.required));
    } else if (role === 'contractor-owner' || role === 'contractor-worker') {
      form.addControl('companySize', this.fb.control(companySize, Validators.required));
    } else if (role === 'dealer' || role === 'consultant') {
      form.addControl('companyName', this.fb.control(companyName, Validators.required));
    }
  }
}
